/**
 * Super Admin · Database Backups
 *
 * One-click MongoDB → zip → S3 backup with full history and per-row
 * delete. Powered by ``/api/super/backups`` (POST/GET/DELETE).
 *
 * UX:
 *  - Big "Backup now" CTA at the top with a spinner-locked button so
 *    the operator can't accidentally double-click during the dump.
 *  - History table with S3 URL (open + copy), date/time, doc count,
 *    archive size, and a Delete-with-confirm button.
 *  - The latest successful backup highlights with a thin emerald
 *    border so it's findable at a glance.
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, DatabaseBackup, Download, Copy, Trash2, Calendar, FileArchive, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

function fmtBytes(n) {
    if (n == null) return "—";
    const v = Number(n);
    if (v < 1024) return `${v} B`;
    if (v < 1024 ** 2) return `${(v / 1024).toFixed(1)} KB`;
    if (v < 1024 ** 3) return `${(v / 1024 ** 2).toFixed(1)} MB`;
    return `${(v / 1024 ** 3).toFixed(2)} GB`;
}

export default function SuperBackups() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/super/backups");
            setRows(data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load backups");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const latestId = useMemo(() => rows[0]?.id, [rows]);

    const onBackup = async () => {
        setBusy(true);
        toast.message("Backup running — this can take 30s+ on large databases.");
        try {
            const { data } = await api.post("/super/backups");
            toast.success(`Backup ready — ${data.collections} collections, ${data.documents.toLocaleString()} documents`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Backup failed");
        } finally { setBusy(false); }
    };

    const onDelete = async (id) => {
        try {
            await api.delete(`/super/backups/${id}`);
            toast.success("Backup deleted from S3");
            setConfirmDeleteId(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const onCopy = (url) => {
        try {
            navigator.clipboard.writeText(url);
            toast.success("S3 link copied");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not copy");
        }
    };

    return (
        <div data-testid="super-backups-page" className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold inline-flex items-center gap-2">
                        <DatabaseBackup size={24} /> Database Backups
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Dump every collection in this database to a single <code className="text-[11px] bg-muted px-1 rounded">.zip</code> archive
                        (JSONL per collection, BSON-aware) and upload it to S3. Each backup is timestamped
                        and stored under the <code className="text-[11px] bg-muted px-1 rounded">backups/</code> key prefix.
                    </p>
                </div>
                <button
                    data-testid="backup-now-btn"
                    onClick={onBackup}
                    disabled={busy}
                    className="bg-emerald-600 text-white rounded-xl px-5 py-3 font-bold inline-flex items-center gap-2 disabled:opacity-60 shadow-sm hover:bg-emerald-700 transition"
                >
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <DatabaseBackup size={16} />}
                    {busy ? "Backing up…" : "Backup now"}
                </button>
            </div>

            <div className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="px-5 py-3 border-b border-border bg-muted/30 flex items-center justify-between flex-wrap gap-2">
                    <div className="font-semibold text-sm">
                        History <span className="text-muted-foreground font-normal">· {rows.length} backup{rows.length === 1 ? "" : "s"}</span>
                    </div>
                    <button
                        onClick={load}
                        className="text-xs font-bold text-muted-foreground hover:text-foreground"
                        data-testid="backups-refresh"
                    >
                        Refresh
                    </button>
                </div>

                {loading ? (
                    <div className="text-center text-sm text-muted-foreground py-12">
                        <Loader2 className="inline animate-spin mr-2" size={16} /> Loading…
                    </div>
                ) : rows.length === 0 ? (
                    <div className="text-center py-16 text-muted-foreground">
                        <FileArchive size={36} className="mx-auto mb-2 opacity-40" />
                        <p className="text-sm">No backups yet. Click <strong>&quot;Backup now&quot;</strong> to create your first one.</p>
                    </div>
                ) : (
                    <ul className="divide-y divide-border">
                        {rows.map((r) => {
                            const isLatest = r.id === latestId;
                            return (
                                <li
                                    key={r.id}
                                    data-testid={`backup-row-${r.id}`}
                                    className={`px-5 py-4 flex flex-col md:flex-row md:items-center gap-3 ${isLatest ? "border-l-4 border-l-emerald-500" : ""}`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Calendar size={14} className="text-muted-foreground" />
                                            <span className="font-bold text-sm" data-testid={`backup-row-date-${r.id}`}>
                                                {fmtDate(r.created_at)}
                                            </span>
                                            {isLatest && (
                                                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                                                    Latest
                                                </span>
                                            )}
                                            <span className="text-[11px] text-muted-foreground">
                                                · {r.collections} collections · {Number(r.documents).toLocaleString()} docs · {fmtBytes(r.size_bytes)}
                                            </span>
                                        </div>
                                        <a
                                            href={r.s3_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="block mt-1 text-xs text-primary truncate hover:underline"
                                            data-testid={`backup-row-url-${r.id}`}
                                            title={r.s3_url}
                                        >
                                            {r.s3_url}
                                        </a>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <button
                                            onClick={() => onCopy(r.s3_url)}
                                            data-testid={`backup-copy-${r.id}`}
                                            className="bg-muted text-foreground border border-border rounded-lg px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 hover:bg-muted/80"
                                        >
                                            <Copy size={12} /> Copy
                                        </button>
                                        <a
                                            href={r.s3_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`backup-download-${r.id}`}
                                            className="bg-sky-600 text-white rounded-lg px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 hover:bg-sky-700"
                                        >
                                            <Download size={12} /> Download
                                        </a>
                                        <button
                                            onClick={() => setConfirmDeleteId(r.id)}
                                            data-testid={`backup-delete-${r.id}`}
                                            className="bg-rose-600 text-white rounded-lg px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 hover:bg-rose-700"
                                        >
                                            <Trash2 size={12} /> Delete
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {confirmDeleteId && (
                <div
                    className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4"
                    onClick={() => setConfirmDeleteId(null)}
                    data-testid="backup-delete-confirm"
                >
                    <div
                        className="bg-card border border-border rounded-2xl max-w-md w-full p-5 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center gap-2 mb-3">
                            <AlertTriangle size={18} className="text-rose-600" />
                            <h3 className="font-bold text-base">Delete this backup?</h3>
                        </div>
                        <p className="text-sm text-muted-foreground mb-4">
                            The archive will be permanently removed from S3 and the history row will be cleared. This cannot be undone.
                        </p>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setConfirmDeleteId(null)}
                                className="bg-muted text-foreground border border-border rounded-lg px-4 py-2 text-sm font-bold"
                                data-testid="backup-delete-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => onDelete(confirmDeleteId)}
                                className="bg-rose-600 text-white rounded-lg px-4 py-2 text-sm font-bold"
                                data-testid="backup-delete-confirm-btn"
                            >
                                Delete backup
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
